// src/utils/authFetch.js
// ---------------------------------------------------------------------------
// Installs a thin wrapper around window.fetch so EVERY call to the backend API
// automatically carries   Authorization: Bearer <jwt>   (token comes from the
// logged-in user object saved at login in sessionStorage).
//
//  * The public login call (/api/login) is left untouched.
//  * Requests to anything that is not "/api/..." are left untouched.
//  * A 401 from a protected route (expired / invalid token, revoked or deleted
//    account) clears the session and tells the app to go back to the login page.
//
// Import ONCE, before the app renders:   import './utils/authFetch';
// ---------------------------------------------------------------------------
import { deviceSignalsHeader } from './deviceDetection';

const SESSION_KEY = 'user_ac';
export const UNAUTHORIZED_EVENT = 'user_ac:unauthorized';
// Fired when the server answers 403 MOBILE_ACCESS_BLOCKED / DEVICE_UNVERIFIED.
export const MOBILE_BLOCKED_EVENT = 'user_ac:mobile-blocked';

export const getAuthToken = () => {
    try {
        const raw = sessionStorage.getItem(SESSION_KEY);
        return raw ? JSON.parse(raw).token || null : null;
    } catch (e) {
        return null;
    }
};

const urlOf = (input) => (typeof input === 'string' ? input : (input && input.url) || '');
const isApiCall = (url) => /\/api\//.test(url);
const isLoginCall = (url) => /\/api\/login(\?|$)/.test(url);

if (typeof window !== 'undefined' && window.fetch && !window.fetch.__r360Wrapped) {
    const originalFetch = window.fetch.bind(window);

    const wrapped = async (input, init = {}) => {
        const url = urlOf(input);
        if (!isApiCall(url)) return originalFetch(input, init);

        const headers = new Headers(init.headers || (typeof input !== 'string' && input.headers) || {});
        // Device signals go on EVERY API call (login included) — the server uses them,
        // together with its own view of the request, to enforce the mobile-access policy.
        try { headers.set('X-Device-Signals', deviceSignalsHeader()); } catch (e) { /* ignore */ }
        if (isLoginCall(url)) return originalFetch(input, { ...init, headers });

        const token = getAuthToken();
        if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);

        const response = await originalFetch(input, { ...init, headers });

        if (response.status === 403) {
            let code = '', reasons = [];
            try { const b = await response.clone().json(); code = b.code || ''; reasons = Array.isArray(b.reasons) ? b.reasons : []; } catch (e) { /* not JSON */ }
            if (code === 'MOBILE_ACCESS_BLOCKED' || code === 'DEVICE_UNVERIFIED') {
                window.dispatchEvent(new CustomEvent(MOBILE_BLOCKED_EVENT, { detail: { code, reasons } }));
            }
        }

        if (response.status === 401) {
            let code = '';
            try { code = (await response.clone().json()).code || ''; } catch (e) { /* not JSON */ }
            try { sessionStorage.removeItem(SESSION_KEY); } catch (e) { /* ignore */ }
            window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT, { detail: { code } }));
        }
        return response;
    };
    wrapped.__r360Wrapped = true;
    window.fetch = wrapped;
}